/**
 * reconcileConfirmations (Inventory Screens Step 4.3c-i) — spend the drain's
 * captured `saved` confirmations onto local record state, then dequeue.
 *
 * 4.3b's drain marks outcomes on QUEUE ITEMS only; this sweep is the first
 * code to mutate field-record rows. Per saved item, inside ONE rw transaction
 * over [records, syncQueue] (per-item atomic, per-item ISOLATED — a bad item
 * warns and the sweep continues):
 *
 *   absorb   — THE ONE RULE (PN-STALE B2 spec v1.3 §2): absorb only what
 *              this confirmed item SENT. For each key in item.payload.fields
 *              the server did not drop, baseline takes the sent value and the
 *              flag is recomputed with sameFieldValue. Keys the item did not
 *              carry, and dropped keys, are untouched — baseline, working and
 *              flag. working is never re-cloned from baseline: that re-clone
 *              is what made an edit made after the Save read as confirmed
 *              (Defect B).
 *   etag     — record.etag = item._etag (the fresh basis the next capture
 *              rides on).
 *   re-key   — stamp minted doc-ids onto child entries across
 *              part_numbers/extra_* and asset_tags/extra_* by VALUE match
 *              against the item's id-maps ({trimmed_value: doc_id}). Never
 *              by array position. Duplicate values collapse to one map id —
 *              a Step-5 edit-path concern; tolerated, never fatal.
 *   children — for each group the payload carried: the removal list minus
 *              the ids the item carried (key deleted when empty), and the
 *              group's flag cleared only when the list is empty AND the
 *              row's group still matches what was sent — compared BEFORE the
 *              mint re-key (spec v1.3 §2.4). Groups not sent: untouched.
 *   signal   — record.last_dropped = dropped_fields when present (Step 5
 *              surfaces "these edits didn't take"), else removed.
 *   dequeue  — the item is DELETED (true dequeue). conflict / error /
 *              not_found items are never read or touched here — review-gated,
 *              never-drop, Step 5 owns them.
 *
 * 4.3d-A, extended 5.3 Phase 2 (D-5.3-4): a saved media_add stamps the
 * record's optimistic media entry (matched by outbox_id) with the server
 * identity and clears pending before dequeue; entry-absent is a no-op and
 * re-Load still owns media truth. media_remove items DEQUEUE ONLY. Only
 * field_capture items (or type-less v1 items) take the promotion path.
 *
 * Idempotent by construction: a re-run after (or crash before) any item's
 * commit re-does no-op promotes/re-keys or re-sweeps the still-present item.
 * A `saved` item whose record row is gone (purged device, pre-re-Load) is
 * left `saved` and skipped — non-wedging; the next re-Load self-heals.
 *
 * Pure local state — NO network calls. Uses fieldDb directly (fieldStore is
 * deliberately untouched; only its exported ensureOpen guard is shared).
 */

import { fieldDb } from '../db/fieldDb';
import { ensureOpen } from '../db/fieldStore';
import { getQueueByStatus, enqueueFieldCapture, releaseHeldCapture } from './fieldOutbox';
import {
  CHILD_SENTINELS, REMOVAL_LIST_KEYS, mergedPartNumbers, mergedAssetTags,
} from './buildCapturePayload';
import { sameFieldValue } from '../data/sameFieldValue';

// The child groups a field_capture can carry, each with the builder that made
// its payload array — the reconcile comparison must use the same one (§2.4).
const CHILD_GROUPS = [
  ['part_numbers', mergedPartNumbers],
  ['asset_tags', mergedAssetTags],
];

// Create-stamped fields become server-authoritative at reconcile: promoted
// into baseline and cleared from dirty (D-5.2b-D.2).
// supplier_tool_id LEFT this list with P2-5 Phase 1 (B-218), as oem did in
// a7f7825: TOOL ID is editable on a pending row, and an edit made after the
// create was sent must survive the re-key and ride the follow-up capture
// rather than be discarded. The create draft always dirties it, so every
// field create's follow-up now carries it too — the same value, a no-op write.
// found and added_by_tmg LEFT it too (Bob P2-2 part C, 2026-10-28): the create
// form seeds both checked and the rep may change either, there or on the pending
// row in edit mode. The server's stamp stays in baseline; the rep's value rides
// the follow-up capture. A row that never dirtied them takes the stamp, as before.
const CREATE_STAMPED_KEYS = ['site_id'];

function rekeyChildren(entries, idMap, label, warnings) {
  for (const entry of entries || []) {
    const value = typeof entry.value === 'string' ? entry.value.trim() : entry.value;
    const mintedId = idMap ? idMap[value] : undefined;
    if (mintedId) {
      entry.id = mintedId;
    } else if (!entry.id) {
      warnings.push(`${label} entry "${value}" has no id and no map entry`);
      console.warn(`[fieldReconcile] ${label} entry "${value}" has no id and no map entry — left as-is`);
    }
  }
}

/**
 * Sweep all status==='saved' queue items onto their records.
 *
 * `rekeyed` (P2-2 B): one { pending_id, record } per tool_create that this
 * sweep actually re-keyed — the temp id and the real row just written under
 * the server id. The host grid is a one-shot snapshot that never re-reads
 * Dexie, so this is the only way the re-keyed row reaches it before the next
 * site entry. A pair is collected only after its transaction COMMITS: a pair
 * for a rolled-back write would hand the host a row Dexie does not hold.
 * @returns {Promise<{reconciled: number, skipped_absent: number, warnings: string[],
 *   rekeyed: Array<{pending_id: string, record: Object}>}>}
 */
export async function reconcileConfirmations() {
  const summary = { reconciled: 0, skipped_absent: 0, warnings: [], rekeyed: [] };

  await ensureOpen();
  const savedItems = await getQueueByStatus('saved'); // FIFO; 'saved' ONLY

  for (const item of savedItems) {
    // Set by the tool_create re-key branch only; pushed to summary.rekeyed
    // after the transaction resolves, so a rolled-back item contributes none.
    let rekeyedPair = null;
    // Set by the field_capture branch when the row holds a request (same-row
    // race fix, 2026-10-28); spent after the transaction commits, once the new
    // etag is on the row and this item is out of the queue.
    let releaseId = null;
    try {
      await fieldDb.transaction('rw', fieldDb.records, fieldDb.syncQueue, async () => {
        // 4.3d-A media arm, EXTENDED for 5.3 Phase 2 (D-5.3-4): a saved
        // media_add now stamps the record's OPTIMISTIC entry (matched by
        // outbox_id) with the server identity {media_id, blob_path, blob_url}
        // and clears its pending flag, inside this same txn, before the
        // dequeue. The object-URL preview is DELIBERATELY NOT revoked here:
        // the open modal renders a STALE prop copy of this entry (the
        // backlogged modal-liveness gap), and revoking at stamp time makes a
        // fresh <img> mount against that copy render blank. Left in place it
        // is a harmless fallback — the live entry prefers the stamped
        // blob_url via the sas_url → blob_url → object_url chain. The revoke
        // has exactly two lifecycle homes: delete (handleMediaDelete) and
        // re-Load supersession (fieldStore.mergeRecord). Entry-absent
        // (re-Load's media-always-fresh merge clobbered it, or the row is
        // gone) is a clean no-op — re-Load still owns media truth (D-4.AD),
        // and media_remove still dequeues ONLY.
        if (item.type === 'media_add' || item.type === 'media_remove') {
          if (item.type === 'media_add') {
            const mediaRecord = await fieldDb.records.get(item.record_id);
            const entry = mediaRecord
              ? (mediaRecord.media || []).find((m) => m.outbox_id === item.outbox_id)
              : null;
            if (entry) {
              entry.media_id = item.media_id ?? entry.media_id ?? null;
              entry.blob_path = item.blob_path ?? entry.blob_path ?? null;
              entry.blob_url = item.blob_url ?? entry.blob_url ?? null;
              delete entry.pending;
              await fieldDb.records.put(mediaRecord);
            }
          }
          await fieldDb.syncQueue.delete(item.outbox_id);
          summary.reconciled += 1;
          return;
        }

        // 5.2b (D-5.2b-D): tool_create identity re-key — temp → server id.
        if (item.type === 'tool_create') {
          const serverId = item.server_tool_id;
          if (!serverId) {
            // 2xx-with-unusable-body arm: the doc exists server-side but we
            // have no identity to re-key onto. Leave the item saved (non-
            // wedging); the next re-Load surfaces the real tool.
            summary.skipped_absent += 1;
            summary.warnings.push(`tool_create ${item.outbox_id} saved without server identity — left for re-Load`);
            console.warn(`[fieldReconcile] tool_create ${item.outbox_id} has no server_tool_id — left saved`);
            return;
          }

          const doc = item.created_doc || {};
          const temp = await fieldDb.records.get(item.record_id); // may be absent (queue-only create)

          // D.1 — record_id is the keyPath: re-key = DELETE temp row + PUT
          // real row, never an in-place mutate.
          // D.2 — baseline starts from the created doc (server truth for the
          // create-stamped set); surviving working/dirty from the temp row
          // carries forward MINUS the create-stamped keys.
          const baseline = {
            ...doc,
            id: serverId,
            tool_id: serverId,
            site_id: item.site_id,
          };
          const carriedDirty = { ...(temp?.dirty || {}) };
          for (const k of CREATE_STAMPED_KEYS) delete carriedDirty[k];
          // Seed baseline keys for surviving dirty scalars (null, never the
          // working value). Before PN-STALE B2 Phase 3 this was load-bearing:
          // the follow-up field_capture's promote loop iterated baseline keys
          // and its re-clone wiped any freshly-created scalar not seeded here.
          // The one rule now assigns baseline from the sent payload directly,
          // so the seed no longer decides anything; it stays, harmless, and
          // gives the follow-up a `from` of null rather than absent.
          // (CHILD_SENTINELS is a Set → .has; child groups live outside
          // baseline.)
          for (const k of Object.keys(carriedDirty)) {
            if (!(k in baseline) && !CHILD_SENTINELS.has(k)) baseline[k] = null;
          }
          const working = structuredClone(baseline);
          if (temp?.working) {
            for (const k of Object.keys(carriedDirty)) {
              if (k in temp.working) working[k] = temp.working[k];
            }
          }

          const realRow = {
            record_id: serverId,
            tool_id: serverId,
            site_id: item.site_id,
            etag: item._etag ?? null, // null today — 201 body is underscore-stripped
            baseline,
            working,
            dirty: carriedDirty,
            part_numbers: temp?.part_numbers || [],
            extra_part_numbers: temp?.extra_part_numbers || [],
            asset_tags: temp?.asset_tags || [],
            extra_asset_tags: temp?.extra_asset_tags || [],
            // 5.3 Phase 3b (D-5.3-P3): carry the temp row's OPTIMISTIC media
            // entries onto the real row — each entry's outbox_id preserved
            // byte-for-byte (it is the join key the cycle-2 media_add stamp
            // arm matches on). object_urls ride along UNREVOKED (revoke homes
            // stay delete + mergeRecord). No temp media → [] as before.
            media: temp?.media || [],
            cached_at: new Date().toISOString(),
          };

          if (temp) await fieldDb.records.delete(item.record_id);
          await fieldDb.records.put(realRow);
          // P2-2 B: the pair the host needs, held until commit (see below).
          rekeyedPair = { pending_id: item.record_id, record: realRow };

          // D.4 — DEPENDENT RE-KEY (live since 5.3 Phase 3b): any queued item
          // still referencing the temp record_id (media_adds captured against
          // a pending-* tool, drain-guard-skipped until now) gets its
          // record_id/tool_id rewritten to serverId inside this same txn,
          // BEFORE the dequeue below. The modify also matches THIS
          // tool_create item (same record_id) — harmless: outbox_id is the
          // syncQueue pk and modify never touches it, so the delete that
          // follows still resolves.
          await fieldDb.syncQueue.where('record_id').equals(item.record_id)
            .modify({ record_id: serverId, tool_id: serverId });

          await fieldDb.syncQueue.delete(item.outbox_id);
          summary.reconciled += 1;

          // D.3 — follow-up capture when dirt survives the create. Requires a
          // real etag basis; the create 201 carries none today (landmine), so
          // the follow-up only fires once an etag exists — otherwise the dirt
          // stays local and flushes after the next re-Load refreshes the basis.
          if (Object.keys(carriedDirty).length > 0) {
            if (realRow.etag) {
              try {
                await enqueueFieldCapture(realRow);
              } catch (fuErr) {
                summary.warnings.push(`follow-up capture enqueue failed for ${serverId}: ${fuErr.message}`);
                console.error('[fieldReconcile] follow-up capture enqueue failed:', fuErr);
              }
            } else {
              summary.warnings.push(`tool_create ${serverId} has surviving dirt but no etag basis — follow-up deferred to re-Load`);
              console.warn(`[fieldReconcile] ${serverId}: dirt survives create but no etag — follow-up deferred to re-Load`);
            }
          }
          return;
        }

        const record = await fieldDb.records.get(item.record_id);
        if (!record) {
          // Non-wedging: leave the item 'saved'; the next re-Load self-heals.
          summary.skipped_absent += 1;
          summary.warnings.push(`record ${item.record_id} absent — item ${item.outbox_id} left saved`);
          console.warn(`[fieldReconcile] record ${item.record_id} absent — item ${item.outbox_id} left saved`);
          return;
        }

        // (a) THE ONE RULE (PN-STALE B2 spec v1.3 §2): absorb only what this
        // confirmed item actually SENT; leave everything else exactly as it
        // is. The rule it replaces promoted the row's CURRENT working for
        // every baseline key, re-cloned working from baseline and cleared all
        // of dirty — so an edit made after the Save but before the drain read
        // as confirmed, and reverted at the next clean Load (Defect B).
        //
        // Scalars. For each key the payload carried that the server did not
        // drop, baseline takes the SENT value (the 200 carries no values,
        // spec §3 Q1), and the flag is recomputed with sameFieldValue: the key
        // is DELETED when working still equals what was sent, and kept when
        // the rep has edited it again since. Deleted, never set false — dirty
        // is a sentinel set, read by key presence (isDirtyRecord,
        // buildCapturePayload). An absent field needs no seed: baseline is
        // assigned the sent value directly.
        //
        // Keys the item did not carry: untouched, baseline and flag alike.
        // Dropped keys (§2.3): untouched too — baseline, working and flag. The
        // server refused them; the row keeps the rep's value and stays dirty,
        // and last_dropped below records the refusal. (Before B2 the re-clone
        // reverted a dropped edit and cleared its flag, with no visible signal.)
        const payload = item.payload || {};
        const dropped = new Set(item.dropped_fields || []);
        if (!record.dirty) record.dirty = {};
        for (const [key, sent] of Object.entries(payload.fields || {})) {
          if (dropped.has(key)) continue;
          record.baseline[key] = sent.to;
          if (sameFieldValue(record.working[key], record.baseline[key])) {
            delete record.dirty[key];
          } else {
            record.dirty[key] = true;
          }
        }

        // (b) Child groups the payload carried. A group NOT sent is untouched:
        // its list and its flag. A sent group's removal list loses exactly the
        // ids this item carried (set difference, never a wholesale clear —
        // removals made after the Save are not yet sent), and the key goes
        // when nothing is left. Its flag clears only when the list is empty
        // AND the row's group still matches what was sent.
        //
        // ⚠️ ORDER (spec v1.3 §2.4): this comparison runs BEFORE the mint
        // re-key in (d), through the same builders that made the payload. A
        // minted id is a server effect, not a user edit; compared after the
        // re-key, the row would carry ids the payload lacked, never match, and
        // the group would stay dirty forever.
        for (const [group, build] of CHILD_GROUPS) {
          if (!Array.isArray(payload[group])) continue;
          const listKey = REMOVAL_LIST_KEYS[group];
          const sentRemovals = new Set(payload[listKey] || []);
          const remaining = (record[listKey] || []).filter((id) => !sentRemovals.has(id));
          if (remaining.length > 0) {
            record[listKey] = remaining;
          } else {
            delete record[listKey];
          }
          if (remaining.length === 0 && sameFieldValue(build(record), payload[group])) {
            delete record.dirty[group];
          } else {
            record.dirty[group] = true;
          }
        }

        // (c) Carry the fresh etag — the basis for the next capture.
        record.etag = item._etag;

        // (d) Re-key minted children by value match, never by position.
        rekeyChildren(
          [...(record.part_numbers || []), ...(record.extra_part_numbers || [])],
          item.part_number_ids, 'part_numbers', summary.warnings
        );
        rekeyChildren(
          [...(record.asset_tags || []), ...(record.extra_asset_tags || [])],
          item.asset_tag_ids, 'asset_tags', summary.warnings
        );

        // (f) Dropped signal for Step 5 ("these edits didn't take").
        if (item.dropped_fields && item.dropped_fields.length > 0) {
          record.last_dropped = item.dropped_fields;
        } else if ('last_dropped' in record) {
          delete record.last_dropped;
        }

        // (g) + (h) Commit the record, dequeue the item — atomically.
        await fieldDb.records.put(record);
        await fieldDb.syncQueue.delete(item.outbox_id);
        summary.reconciled += 1;
        if (record.capture_held) releaseId = record.record_id;
      });
      // Reached only when the transaction committed.
      if (rekeyedPair) summary.rekeyed.push(rekeyedPair);
      if (releaseId) {
        try {
          await releaseHeldCapture(releaseId);
        } catch (releaseErr) {
          // The mark is persisted; the next reconcile or enqueue for the record spends it.
          summary.warnings.push(`held capture release failed for ${releaseId}: ${releaseErr.message}`);
          console.error(`[fieldReconcile] held capture release failed for ${releaseId}:`, releaseErr);
        }
      }
    } catch (err) {
      // Per-item isolation: log and keep sweeping — never abort the sweep.
      summary.warnings.push(`item ${item.outbox_id} failed: ${err.message}`);
      console.error(`[fieldReconcile] item ${item.outbox_id} failed — sweep continues:`, err);
    }
  }

  if (savedItems.length > 0) {
    console.log('[fieldReconcile] sweep complete:', JSON.stringify({
      swept: savedItems.length,
      reconciled: summary.reconciled,
      skipped_absent: summary.skipped_absent,
      warnings: summary.warnings.length,
    }));
  }
  return summary;
}
